import React, { useEffect, useMemo, useRef, useState } from "react";
import MaterialTable from "material-table";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { ThemeProvider } from "@mui/material";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { normalizeDate, sendUserNotification } from "../../../utilities";
import { getWindowForms } from "../../../components/Forms/api";
import { getWindowDoorUsers } from "../../Admin/Utilities/api";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import { useAppMuiTheme } from "../../../theme/MuiThemeBridge";
import { icons } from "../../../components/Utilities/UtilitiesWD";
import { PageHeader, PageShell } from "../../../components/UI/Page";
import {
  EmptyState,
  LoadingState,
  StatTile,
} from "../../../components/UI/States";
import {
  Banner,
  FilterChip,
  Pill,
  Segmented,
  inputSmCls,
  mtComponents,
  mtHeaderStyle,
  mtRowStyle,
  mtSearchFieldStyle,
  mtStyle,
  waitTone,
} from "../../../components/WindowDoor/wdUi";

const ACTIVE_STATUSES = [
  "Submitted",
  "Under Review",
  "Awaiting Sales Approval",
  "Needs Revision",
  "Reviewing Revisions",
];
const COMPLETED_STATUSES = ["Approved", "Converted to Order"];
const ALL_STATUSES = [...ACTIVE_STATUSES, ...COMPLETED_STATUSES, "Cancelled"];

/* Dot hexes double as workload-bar segment fills; the active-status
   sequence is CVD/contrast validated on both light and dark surfaces. */
const STATUS_META = {
  Submitted: {
    dot: "#3B82F6",
    chip: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  },
  "Under Review": {
    dot: "#D97706",
    chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
  "Awaiting Sales Approval": {
    dot: "#6366F1",
    chip:
      "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  },
  "Needs Revision": {
    dot: "#F43F5E",
    chip: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  },
  "Reviewing Revisions": {
    dot: "#D946EF",
    chip:
      "bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300",
  },
  Approved: {
    dot: "#10B981",
    chip:
      "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  "Converted to Order": {
    dot: "#14B8A6",
    chip: "bg-teal-500/15 text-teal-700 dark:text-teal-300",
  },
  Cancelled: {
    dot: "#94A3B8",
    chip: "bg-slate-500/15 text-slate-600 dark:text-slate-300",
  },
};

const TABS = [
  { key: "queue", label: "Incoming Queue" },
  { key: "team", label: "Team Workload" },
  { key: "all", label: "All Submissions" },
];

const statusOf = (entry) => entry?.status || "Submitted";
const isActiveStatus = (status) => ACTIVE_STATUSES.includes(status);
const assigneeIdOf = (entry) =>
  entry?.assigned_to?.userID != null ? Number(entry.assigned_to.userID) : null;

const formatName = (name = "") =>
  name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

const initialsOf = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

const daysSince = (date) =>
  Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 86400000));

// submit_user is an object on newer entries but a plain string on older
// ones; material-table's dotted field lookup chokes on the string form.
const submitterOf = (entry) =>
  entry?.submit_user?.name ||
  entry?.submit_user?.Name ||
  String(entry?.submit_user || "Unknown");

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      STATUS_META[status]?.chip ||
      "bg-slate-500/15 text-slate-600 dark:text-slate-300"
    }`}
  >
    {status}
  </span>
);

const WindowQuoteManager = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const defaultMaterialTheme = useAppMuiTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataStore, setDataStore] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [showAllTeam, setShowAllTeam] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  // Team Workload: which quoter card is expanded into a table, and whether
  // that table shows their active work or everything ever assigned to them.
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [memberScope, setMemberScope] = useState("active");
  const memberTableRef = useRef(null);
  const [queueFormFilter, setQueueFormFilter] = useState("all");
  const [queueSubmitterFilter, setQueueSubmitterFilter] = useState("all");

  const requestedTab = searchParams.get("view");
  const activeTab = TABS.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : "queue";
  const setActiveTab = (key) => setSearchParams({ view: key });

  const refreshEntries = () =>
    getWindowForms((data) => {
      setDataStore(Array.isArray(data) ? data : []);
      setLoaded(true);
    });

  useEffect(() => {
    refreshEntries();
    getWindowDoorUsers(setUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const staffOptions = useMemo(() => users || [], [users]);
  const entries = useMemo(
    () => (Array.isArray(dataStore) ? dataStore : []),
    [dataStore]
  );

  // Oldest first so the queue works as first-in, first-out.
  const queue = useMemo(
    () =>
      entries
        .filter(
          (entry) => !assigneeIdOf(entry) && isActiveStatus(statusOf(entry))
        )
        .sort((a, b) => new Date(a.entry_date) - new Date(b.entry_date)),
    [entries]
  );

  const queueFormOptions = useMemo(
    () =>
      [...new Set(queue.map((entry) => entry?.form_name || "Window Form"))].sort(),
    [queue]
  );
  const queueSubmitterOptions = useMemo(
    () => [...new Set(queue.map((entry) => submitterOf(entry)))].sort(),
    [queue]
  );
  const filteredQueue = queue.filter((entry) => {
    if (
      queueFormFilter !== "all" &&
      (entry?.form_name || "Window Form") !== queueFormFilter
    ) {
      return false;
    }
    if (
      queueSubmitterFilter !== "all" &&
      submitterOf(entry) !== queueSubmitterFilter
    ) {
      return false;
    }
    return true;
  });

  // Quoter roster: the assignable staff list plus anyone already carrying
  // assignments, so new quoters show up without a code change.
  const teamStats = useMemo(() => {
    const roster = new Map();
    staffOptions.forEach((option) => {
      if (option?.UserID != null) {
        roster.set(Number(option.UserID), {
          id: Number(option.UserID),
          name: option.Name || "Unknown",
        });
      }
    });
    entries.forEach((entry) => {
      const id = assigneeIdOf(entry);
      if (id != null && !roster.has(id)) {
        roster.set(id, { id, name: entry.assigned_to?.name || "Unknown" });
      }
    });

    return [...roster.values()]
      .map((member) => {
        const memberEntries = entries.filter(
          (entry) => assigneeIdOf(entry) === member.id
        );
        const byStatus = {};
        memberEntries.forEach((entry) => {
          const status = statusOf(entry);
          byStatus[status] = (byStatus[status] || 0) + 1;
        });
        const active = memberEntries.filter((entry) =>
          isActiveStatus(statusOf(entry))
        );
        return {
          ...member,
          byStatus,
          activeCount: active.length,
          completedCount: COMPLETED_STATUSES.reduce(
            (sum, status) => sum + (byStatus[status] || 0),
            0
          ),
          oldestActiveDays: active.length
            ? Math.max(...active.map((entry) => daysSince(entry.entry_date)))
            : null,
        };
      })
      .sort(
        (a, b) =>
          b.activeCount - a.activeCount || a.name.localeCompare(b.name)
      );
  }, [entries, staffOptions]);

  const busyTeam = teamStats.filter((member) => member.activeCount > 0);
  const visibleTeam =
    showAllTeam || busyTeam.length === 0 ? teamStats : busyTeam;
  const idleCount = teamStats.length - busyTeam.length;
  const maxActive = Math.max(1, ...teamStats.map((m) => m.activeCount));
  const totalAssignedActive = teamStats.reduce(
    (sum, member) => sum + member.activeCount,
    0
  );

  const selectedMember =
    teamStats.find((member) => member.id === selectedMemberId) || null;
  const selectedMemberAssignedTotal = selectedMember
    ? entries.filter((entry) => assigneeIdOf(entry) === selectedMember.id)
        .length
    : 0;
  const selectedMemberEntries = selectedMember
    ? entries
        .filter((entry) => assigneeIdOf(entry) === selectedMember.id)
        .filter((entry) =>
          memberScope === "active" ? isActiveStatus(statusOf(entry)) : true
        )
        .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date))
    : [];

  useEffect(() => {
    if (selectedMemberId && memberTableRef.current) {
      memberTableRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [selectedMemberId]);

  const awaitingSalesCount = entries.filter(
    (entry) => statusOf(entry) === "Awaiting Sales Approval"
  ).length;
  const completedCount = entries.filter((entry) =>
    COMPLETED_STATUSES.includes(statusOf(entry))
  ).length;
  const oldestQueueDays = queue.length
    ? daysSince(queue[0].entry_date)
    : null;

  const statusCounts = useMemo(() => {
    const counts = {};
    entries.forEach((entry) => {
      const status = statusOf(entry);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [entries]);
  const activeTotal = ACTIVE_STATUSES.reduce(
    (sum, status) => sum + (statusCounts[status] || 0),
    0
  );

  const filterOptions = [
    { key: "active", label: "All Active", count: activeTotal },
    ...ALL_STATUSES.map((status) => ({
      key: status,
      label: status,
      count: statusCounts[status] || 0,
    })),
    { key: "everything", label: "Everything", count: entries.length },
  ];

  const tableData = useMemo(() => {
    return entries
      .filter((entry) => {
        const status = statusOf(entry);
        if (statusFilter === "active" && !isActiveStatus(status)) return false;
        if (
          statusFilter !== "active" &&
          statusFilter !== "everything" &&
          status !== statusFilter
        )
          return false;
        if (assigneeFilter === "unassigned") return !assigneeIdOf(entry);
        if (assigneeFilter !== "all")
          return assigneeIdOf(entry) === Number(assigneeFilter);
        return true;
      })
      .sort((a, b) => new Date(b.entry_date) - new Date(a.entry_date));
  }, [entries, statusFilter, assigneeFilter]);

  const assignEntry = async (entryId, value) => {
    const member = staffOptions.find(
      (option) => Number(option.UserID) === Number(value)
    );
    const payload = member
      ? {
          assigned_to: {
            userID: member.UserID,
            name: member.Name,
            email: member.EmailAddress,
          },
        }
      : { assigned_to: null };
    setSavingId(entryId);
    try {
      await fetch(`/api/forms/misc/windowforms/${entryId}/assign`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      // Tell the quoter they have new work (skip self-assignment).
      if (member?.EmailAddress && member.EmailAddress !== user?.email) {
        const entry = entries.find((e) => e._id === entryId);
        sendUserNotification(
          member.EmailAddress,
          user?.name || "Window Quote Manager",
          `You've been assigned a window RFQ from ${submitterOf(entry)}.`,
          "Window RFQ assigned to you",
          `/windowdoor/staff/windowforms/${entryId}`
        );
      }
      await refreshEntries();
    } finally {
      setSavingId(null);
    }
  };

  const AssignSelect = ({ entry }) => (
    <select
      aria-label="Assign quoter"
      className={`${inputSmCls} w-44`}
      value={assigneeIdOf(entry) || ""}
      disabled={savingId === entry._id}
      onChange={(event) => assignEntry(entry._id, event.target.value)}
    >
      <option value="">Unassigned</option>
      {staffOptions.map((option) => (
        <option key={option.UserID} value={option.UserID}>
          {formatName(option.Name)}
        </option>
      ))}
    </select>
  );

  const ViewButton = ({ entry }) => (
    <button
      type="button"
      className="app-btn app-btn-secondary app-btn-sm"
      onClick={() => navigate(`/windowdoor/quote-manager/${entry?._id}`)}
    >
      View
    </button>
  );

  const tableIcons = icons;

  const tableStyle = mtStyle;

  const tableOptions = {
    pageSize: 10,
    emptyRowsWhenPaging: false,
    pageSizeOptions: [5, 10, 15, 25],
    headerStyle: mtHeaderStyle,
    searchFieldStyle: mtSearchFieldStyle,
    rowStyle: mtRowStyle,
  };

  const tableLocalization = {
    body: {
      emptyDataSourceMessage: (
        <EmptyState
          compact
          icon={SearchOffIcon}
          title="No submissions"
          description="Nothing matches the current filters."
        />
      ),
    },
  };

  const submissionColumns = [
    {
      title: "Received",
      field: "entry_date",
      defaultSort: "desc",
      customSort: (a, b) => new Date(a.entry_date) - new Date(b.entry_date),
      render: (rowData) => (
        <span className="whitespace-nowrap tabular-nums">
          {normalizeDate(new Date(rowData?.entry_date))}
        </span>
      ),
    },
    {
      title: "Customer / Job",
      field: "customer",
      render: (rowData) => (
        <div className="flex flex-col">
          <span className="font-semibold">
            {rowData?.customer || "Unknown customer"}
          </span>
          {(rowData?.account || rowData?.job) && (
            <span className="app-muted text-xs">
              {[rowData?.account, rowData?.job].filter(Boolean).join(" • ")}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Form",
      field: "form_name",
      render: (rowData) => rowData?.form_name || "Window Form",
    },
    {
      title: "Submitted By",
      customFilterAndSearch: (term, rowData) =>
        submitterOf(rowData).toLowerCase().includes(term.toLowerCase()),
      customSort: (a, b) => submitterOf(a).localeCompare(submitterOf(b)),
      render: (rowData) => formatName(submitterOf(rowData)),
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => <StatusBadge status={statusOf(rowData)} />,
    },
    {
      title: "Assigned To",
      sorting: false,
      customFilterAndSearch: (term, rowData) =>
        (rowData?.assigned_to?.name || "Unassigned")
          .toLowerCase()
          .includes(term.toLowerCase()),
      render: (rowData) => <AssignSelect entry={rowData} />,
    },
    {
      title: "",
      sorting: false,
      render: (rowData) => <ViewButton entry={rowData} />,
    },
  ];

  // Queue table: no Status/Assigned To columns (everything here is an
  // unassigned active submission); adds the waiting-days badge instead.
  const queueColumns = [
    {
      title: "Received",
      field: "entry_date",
      defaultSort: "asc",
      customSort: (a, b) => new Date(a.entry_date) - new Date(b.entry_date),
      render: (rowData) => (
        <span className="whitespace-nowrap tabular-nums">
          {normalizeDate(new Date(rowData?.entry_date))}
        </span>
      ),
    },
    {
      title: "Waiting",
      sorting: false,
      render: (rowData) => {
        const waitDays = daysSince(rowData?.entry_date);
        return (
          <Pill tone={waitTone(waitDays)}>
            {waitDays} day{waitDays === 1 ? "" : "s"}
          </Pill>
        );
      },
    },
    {
      title: "Customer / Job",
      field: "customer",
      render: (rowData) => (
        <div className="flex flex-col">
          <span className="font-semibold">
            {rowData?.customer || "Unknown customer"}
          </span>
          {(rowData?.account || rowData?.job) && (
            <span className="app-muted text-xs">
              {[rowData?.account, rowData?.job].filter(Boolean).join(" • ")}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Form",
      field: "form_name",
      render: (rowData) => rowData?.form_name || "Window Form",
    },
    {
      title: "Submitted By",
      customFilterAndSearch: (term, rowData) =>
        submitterOf(rowData).toLowerCase().includes(term.toLowerCase()),
      customSort: (a, b) => submitterOf(a).localeCompare(submitterOf(b)),
      render: (rowData) => formatName(submitterOf(rowData)),
    },
    {
      title: "Assign To",
      sorting: false,
      render: (rowData) => <AssignSelect entry={rowData} />,
    },
    {
      title: "",
      sorting: false,
      render: (rowData) => <ViewButton entry={rowData} />,
    },
  ];

  const kpiTiles = [
    {
      label: "Needs assignment",
      value: queue.length,
      detail:
        queue.length > 0
          ? `oldest waiting ${oldestQueueDays} day${
              oldestQueueDays === 1 ? "" : "s"
            }`
          : "queue is clear",
      tab: "queue",
    },
    {
      label: "In progress",
      value: totalAssignedActive,
      detail: `across ${busyTeam.length} quoter${
        busyTeam.length === 1 ? "" : "s"
      }`,
      tab: "team",
    },
    {
      label: "Waiting on sales",
      value: awaitingSalesCount,
      detail: "awaiting sales approval",
      tab: "all",
      status: "Awaiting Sales Approval",
    },
    {
      label: "Completed",
      value: completedCount,
      detail: "approved or converted",
      tab: "all",
      status: "Approved",
    },
  ];

  const renderQueueTab = () => {
    if (queue.length === 0) {
      return (
        <div className="app-card rounded-2xl border">
          <EmptyState
            icon={TaskAltOutlinedIcon}
            title="Queue is clear"
            description="Every submission has been assigned to a quoter. New forms will appear here as they come in."
          />
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="app-muted text-sm">
            {queue.length} submission{queue.length === 1 ? "" : "s"} waiting
            for a quoter, oldest first. Picking a name assigns it immediately.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="Filter by form"
              className={inputSmCls}
              value={queueFormFilter}
              onChange={(event) => setQueueFormFilter(event.target.value)}
            >
              <option value="all">All forms</option>
              {queueFormOptions.map((formName) => (
                <option key={formName} value={formName}>
                  {formName}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by salesperson"
              className={inputSmCls}
              value={queueSubmitterFilter}
              onChange={(event) => setQueueSubmitterFilter(event.target.value)}
            >
              <option value="all">All salespeople</option>
              {queueSubmitterOptions.map((name) => (
                <option key={name} value={name}>
                  {formatName(name)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="app-card overflow-hidden rounded-2xl border">
          <ThemeProvider theme={defaultMaterialTheme}>
            <MaterialTable
              components={mtComponents}
              columns={queueColumns}
              data={filteredQueue}
              title="Incoming Queue"
              icons={tableIcons}
              style={tableStyle}
              options={tableOptions}
              localization={tableLocalization}
            />
          </ThemeProvider>
        </div>
      </div>
    );
  };

  const renderTeamTab = () => (
    <div className="space-y-5">
      {queue.length > 0 && (
        <Banner
          tone="warning"
          className="flex flex-wrap items-center justify-between gap-3"
        >
          <p>
            <span className="font-semibold">
              {queue.length} submission{queue.length === 1 ? "" : "s"}
            </span>{" "}
            still waiting for a quoter.
          </p>
          <button
            type="button"
            className="app-btn app-btn-primary app-btn-sm"
            onClick={() => setActiveTab("queue")}
          >
            Review queue
          </button>
        </Banner>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="app-muted text-sm">
          {totalAssignedActive} active RFQ
          {totalAssignedActive === 1 ? "" : "s"} assigned
          {busyTeam.length > 0 &&
            ` • avg ${(totalAssignedActive / busyTeam.length).toFixed(
              1
            )} per active quoter`}{" "}
          • click a quoter to see their RFQs
        </p>
        {idleCount > 0 && busyTeam.length > 0 && (
          <button
            type="button"
            className="app-link text-sm"
            onClick={() => setShowAllTeam((prev) => !prev)}
          >
            {showAllTeam
              ? "Hide quoters without work"
              : `Show all team members (${idleCount} idle)`}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {ACTIVE_STATUSES.map((status) => (
          <span key={status} className="flex items-center gap-1.5 text-xs">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: STATUS_META[status].dot }}
            />
            <span className="app-muted">{status}</span>
          </span>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visibleTeam.map((member) => (
          <div
            key={member.id}
            role="button"
            tabIndex={0}
            aria-expanded={selectedMemberId === member.id}
            onClick={() =>
              setSelectedMemberId((prev) =>
                prev === member.id ? null : member.id
              )
            }
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setSelectedMemberId((prev) =>
                  prev === member.id ? null : member.id
                );
              }
            }}
            className={`app-card cursor-pointer rounded-2xl border p-5 transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 ${
              selectedMemberId === member.id ? "ring-2 ring-sky-500" : ""
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-500/15 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                  {initialsOf(member.name)}
                </span>
                <span className="truncate font-semibold">
                  {formatName(member.name)}
                </span>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold leading-none tabular-nums">
                  {member.activeCount}
                </p>
                <p className="app-muted mt-0.5 text-xs">active</p>
              </div>
            </div>
            {/* Bar length compares this quoter's load to the busiest quoter */}
            <div className="mt-4 h-2.5 w-full overflow-hidden rounded-full bg-slate-400/20">
              <div
                className="flex h-full overflow-hidden rounded-full"
                style={{ width: `${(member.activeCount / maxActive) * 100}%` }}
              >
                {ACTIVE_STATUSES.filter(
                  (status) => member.byStatus[status]
                ).map((status, index) => (
                  <div
                    key={status}
                    style={{
                      width: `${
                        (member.byStatus[status] / member.activeCount) * 100
                      }%`,
                      backgroundColor: STATUS_META[status].dot,
                      marginLeft: index === 0 ? 0 : 2,
                    }}
                  />
                ))}
              </div>
            </div>
            {member.activeCount > 0 ? (
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {ACTIVE_STATUSES.filter(
                  (status) => member.byStatus[status]
                ).map((status) => (
                  <span
                    key={status}
                    className="flex items-center gap-1.5 text-xs"
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: STATUS_META[status].dot }}
                    />
                    <span className="app-muted">{status}</span>
                    <span className="font-semibold tabular-nums">
                      {member.byStatus[status]}
                    </span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="app-muted mt-3 text-xs">No active RFQs</p>
            )}
            <p className="app-muted mt-4 border-t app-divider pt-3 text-xs">
              {member.completedCount} completed
              {member.oldestActiveDays != null &&
                ` • oldest active ${member.oldestActiveDays} day${
                  member.oldestActiveDays === 1 ? "" : "s"
                }`}
            </p>
          </div>
        ))}
      </div>
      {selectedMember && (
        <div ref={memberTableRef} className="space-y-3 pt-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold">
              {formatName(selectedMember.name)}&apos;s RFQs
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <Segmented
                ariaLabel="Which RFQs"
                value={memberScope}
                onChange={setMemberScope}
                options={[
                  {
                    key: "active",
                    label: `Active (${selectedMember.activeCount})`,
                  },
                  {
                    key: "all",
                    label: `Everything (${selectedMemberAssignedTotal})`,
                  },
                ]}
              />
              <button
                type="button"
                className="app-btn app-btn-ghost app-btn-sm"
                onClick={() => setSelectedMemberId(null)}
              >
                Close
              </button>
            </div>
          </div>
          <div className="app-card overflow-hidden rounded-2xl border">
            <ThemeProvider theme={defaultMaterialTheme}>
              <MaterialTable
                components={mtComponents}
                columns={submissionColumns}
                data={selectedMemberEntries}
                title={`${formatName(selectedMember.name)} — ${
                  memberScope === "active" ? "Active RFQs" : "All Assigned"
                }`}
                icons={tableIcons}
                style={tableStyle}
                options={tableOptions}
                localization={tableLocalization}
              />
            </ThemeProvider>
          </div>
        </div>
      )}
    </div>
  );

  const renderAllTab = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {filterOptions.map((option) => (
            <FilterChip
              key={option.key}
              active={statusFilter === option.key}
              onClick={() => setStatusFilter(option.key)}
              count={option.count}
            >
              {option.label}
            </FilterChip>
          ))}
        </div>
        <select
          aria-label="Filter by quoter"
          className={inputSmCls}
          value={assigneeFilter}
          onChange={(event) => setAssigneeFilter(event.target.value)}
        >
          <option value="all">All quoters</option>
          <option value="unassigned">Unassigned</option>
          {teamStats.map((member) => (
            <option key={member.id} value={member.id}>
              {formatName(member.name)}
            </option>
          ))}
        </select>
      </div>
      <div className="app-card overflow-hidden rounded-2xl border">
        <ThemeProvider theme={defaultMaterialTheme}>
          <MaterialTable
            components={mtComponents}
            columns={submissionColumns}
            data={tableData}
            title="Submissions"
            icons={tableIcons}
            style={tableStyle}
            options={tableOptions}
            localization={tableLocalization}
          />
        </ThemeProvider>
      </div>
    </div>
  );

  const KPI_TONES = ["amber", "sky", "violet", "emerald"];

  return (
    <PageShell size="2xl">
      <PageHeader
        icon={RequestQuoteOutlinedIcon}
        tone="indigo"
        eyebrow="Windows & Doors"
        title="Window Quote Manager"
        description="Incoming RFQ submissions, quoter assignments, and team workload at a glance."
        actions={
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={() => navigate("/windowdoor/quote-manager/settings")}
          >
            <TuneOutlinedIcon fontSize="small" />
            Form Settings
          </button>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpiTiles.map((tile, index) => (
          <StatTile
            key={tile.label}
            label={tile.label}
            value={loaded ? tile.value : "—"}
            hint={tile.detail}
            tone={tile.value > 0 ? KPI_TONES[index] : "default"}
            active={
              activeTab === tile.tab &&
              (!tile.status || statusFilter === tile.status)
            }
            onClick={() => {
              if (tile.status) setStatusFilter(tile.status);
              setActiveTab(tile.tab);
            }}
          />
        ))}
      </div>

      <div className="my-6">
        <Segmented
          ariaLabel="Quote Manager views"
          value={activeTab}
          onChange={setActiveTab}
          options={TABS.map((tab) => ({
            key: tab.key,
            label: tab.label,
            badge:
              tab.key === "queue" && queue.length > 0 ? (
                <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold tabular-nums text-white">
                  {queue.length}
                </span>
              ) : null,
          }))}
        />
      </div>

      {!loaded ? (
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading submissions…" />
        </div>
      ) : activeTab === "queue" ? (
        renderQueueTab()
      ) : activeTab === "team" ? (
        renderTeamTab()
      ) : (
        renderAllTab()
      )}
    </PageShell>
  );
};

export default WindowQuoteManager;
